import { ComputedRef, watch } from "vue"
import { PatientConversationMessageDTO } from "@vitable/asclepius-api-client-ts"
import { useCurrentStaffId } from "@/modules/patient-conversations/composables/current-staff"
import { useAcknowledgePatientConversationMentions } from "@/modules/patient-conversations/composables/patient-conversations"

/**
 * Opening a conversation acknowledges the viewer's own pages (TDP §5.2.6): each loaded message is checked for an
 * open page to the signed-in user and those pages are acknowledged together. Older pages are covered as they load.
 */
export function useAcknowledgeViewerPages(
  patientConversationId: ComputedRef<string>,
  messages: ComputedRef<ReadonlyArray<PatientConversationMessageDTO>>
): void {
  const currentStaffId = useCurrentStaffId()
  const { mutateAsync: acknowledgeMentions } = useAcknowledgePatientConversationMentions()
  // Pages already sent for acknowledgement, so a poll that lands before the server answers does not send them twice
  const inFlight = new Set<string>()

  watch(
    [messages, currentStaffId],
    ([loaded, viewerId]) => {
      if (!viewerId) return
      const mentionIds = loaded
        .flatMap((message) => message.mentions ?? [])
        .filter((mention) => mention.user.id === viewerId && !mention.acknowledged_at && !inFlight.has(mention.id))
        .map((mention) => mention.id)
      if (!mentionIds.length) return

      mentionIds.forEach((id) => inFlight.add(id))
      acknowledgeMentions({ patientConversationId: patientConversationId.value, mentionIds })
        // Left unacknowledged on failure; the next thread poll tries again
        .catch(() => mentionIds.forEach((id) => inFlight.delete(id)))
    },
    { immediate: true }
  )
}
